import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate, openAttachment } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Building, 
  GraduationCap, 
  Users, 
  Calendar, 
  Mail, 
  Globe, 
  MapPin,
  Phone,
  FileText,
  Paperclip,
  Image,
  Download,
  CheckCircle,
  X,
  AlertTriangle
} from 'lucide-react';

const TrainingCenterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [center, setCenter] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [allInstructors, setAllInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [centerRes, instructorsRes, studentsRes, classesRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/training-centers/${id}`),
          axios.get(`${API_URL}/instructors`),
          axios.get(`${API_URL}/persons?training_center_id=${id}&page_size=1000`),
          axios.get(`${API_URL}/classes?training_center_id=${id}&page_size=1000`),
          axios.get(`${API_URL}/courses`)
        ]);
        
        setCenter(centerRes.data);
        setCourses(coursesRes.data);
        setAllInstructors(instructorsRes.data);
        
        // Filter instructors assigned to this training center
        const centerInstructors = instructorsRes.data.filter(
          i => i.training_center_ids?.includes(id)
        );
        setInstructors(centerInstructors);
        
        // Classes are already filtered server-side by training_center_id
        const centerClasses = classesRes.data.items || classesRes.data;
        setClasses(centerClasses);
        
        // Persons are already filtered server-side by training_center_id;
        // additionally include any students who participated in classes here
        const directStudents = studentsRes.data.items || studentsRes.data;
        const studentIdsFromClasses = new Set(directStudents.map(s => s.id));
        centerClasses.forEach(cls => {
          if (cls.date_completed && cls.student_ids) {
            cls.student_ids.forEach(sid => studentIdsFromClasses.add(sid));
          }
        });
        // If classes contain students not in the direct list, fetch those by id
        const missingIds = [...studentIdsFromClasses].filter(sid => !directStudents.find(s => s.id === sid));
        let extraStudents = [];
        if (missingIds.length > 0) {
          const lookupRes = await axios.get(`${API_URL}/persons/lookup?ids=${missingIds.join(',')}`);
          extraStudents = lookupRes.data.items || [];
        }
        setStudents([...directStudents, ...extraStudents]);
        
      } catch (error) {
        toast.error('Failed to fetch training center details');
        navigate('/training-centers');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate]);

  // Helper function to get class status (same logic as Classes.jsx)
  const getClassStatus = (cls) => {
    if (cls.certificates_issued) {
      const course = courses.find(c => c.id === cls.course_id);
      const completionDate = cls.date_completed || cls.date_start;
      if (course && completionDate) {
        const expiryDate = new Date(completionDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + (course.expiration_years || 3));
        const now = new Date();
        
        if (expiryDate < now) {
          return 'expired';
        } else if (expiryDate < new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)) {
          return 'expiring';
        }
      }
      return 'certified';
    } else if (cls.date_completed) {
      return 'completed';
    }
    return 'in_progress';
  };

  const getInstructorNames = (instructorIds) => {
    if (!instructorIds || instructorIds.length === 0) return '-';
    return instructorIds.map(instId => {
      const instructor = allInstructors.find(i => i.id === instId);
      return instructor ? `${instructor.first_name} ${instructor.last_name}` : null;
    }).filter(Boolean).join(', ') || '-';
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (!center) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="training-center-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/training-centers')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Center Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#2C9DE1]/10 rounded-xl flex items-center justify-center">
                <Building className="w-8 h-8 text-[#2C9DE1]" />
              </div>
              <div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {center.name}
                </CardTitle>
                <CardDescription className="mt-1">
                  {center.city && center.country ? `${center.city}, ${center.country}` : 'Training Center'}
                </CardDescription>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate(`/training-centers?edit=${center.id}`)}
              data-testid="edit-center-btn"
            >
              Edit Center
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {center.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Email</p>
                  <p className="font-medium">{center.email}</p>
                </div>
              </div>
            )}
            {center.domain && (
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Domain</p>
                  <p className="font-medium">{center.domain}</p>
                </div>
              </div>
            )}
            {center.primary_contact && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Primary Contact</p>
                  <p className="font-medium">{center.primary_contact}</p>
                </div>
              </div>
            )}
            {center.address && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Address</p>
                  <p className="font-medium">{center.address}</p>
                </div>
              </div>
            )}
            {center.registration_number && (
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Registration #</p>
                  <p className="font-medium">{center.registration_number}</p>
                </div>
              </div>
            )}
            {center.vat_number && (
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">VAT #</p>
                  <p className="font-medium">{center.vat_number}</p>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          {center.attachments?.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Attachments ({center.attachments.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {center.attachments.map((att) => (
                  <button
                    type="button"
                    key={att.id}
                    data-testid={`download-attachment-${att.id}`}
                    onClick={() => openAttachment(`${API_URL}/training-centers/${center.id}/attachments/${att.id}`).catch(() => toast.error('Could not open file'))}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    {getFileIcon(att.content_type)}
                    <span className="text-sm">{att.filename}</span>
                    <Download className="w-3 h-3 text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{instructors.length}</p>
                <p className="text-sm text-slate-500">Instructors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{students.length}</p>
                <p className="text-sm text-slate-500">Students</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{classes.length}</p>
                <p className="text-sm text-slate-500">Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Instructors, Students, Classes */}
      <Tabs defaultValue="instructors" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="instructors" data-testid="tab-instructors">
            Instructors
          </TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students">
            Students
          </TabsTrigger>
          <TabsTrigger value="classes" data-testid="tab-classes">
            Classes
          </TabsTrigger>
        </TabsList>

        {/* Instructors Tab */}
        <TabsContent value="instructors">
          <Card>
            <CardHeader>
              <CardTitle>Instructors</CardTitle>
              <CardDescription>Instructors assigned to this training center</CardDescription>
            </CardHeader>
            <CardContent>
              {instructors.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No instructors assigned to this center</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Instructor #</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {instructors.map((instructor) => (
                      <TableRow key={instructor.id}>
                        <TableCell className="font-medium">
                          {instructor.first_name} {instructor.last_name}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                            {instructor.instructor_number}
                          </span>
                        </TableCell>
                        <TableCell>{instructor.email || '-'}</TableCell>
                        <TableCell>{instructor.mobile || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students">
          <Card>
            <CardHeader>
              <CardTitle>Students</CardTitle>
              <CardDescription>Students trained at this center</CardDescription>
            </CardHeader>
            <CardContent>
              {students.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No students assigned to this center</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>ID Number</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell className="font-medium">
                          <Link 
                            to={`/students/${student.id}`}
                            className="hover:text-[#2C9DE1] hover:underline"
                          >
                            {student.first_name} {student.last_name}
                          </Link>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{student.id_number}</TableCell>
                        <TableCell>{student.email || '-'}</TableCell>
                        <TableCell>{student.mobile || '-'}</TableCell>
                        <TableCell>
                          <Link to={`/students/${student.id}`}>
                            <Button variant="ghost" size="sm">View</Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Classes Tab */}
        <TabsContent value="classes">
          <Card>
            <CardHeader>
              <CardTitle>Classes</CardTitle>
              <CardDescription>Classes conducted at this center</CardDescription>
            </CardHeader>
            <CardContent>
              {classes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No classes at this center</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Completion Date</TableHead>
                      <TableHead className="font-semibold">Type</TableHead>
                      <TableHead className="font-semibold">Course</TableHead>
                      <TableHead className="font-semibold">Instructor(s)</TableHead>
                      <TableHead className="font-semibold">Students</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classes.map((cls) => (
                      <TableRow key={cls.id} className="hover:bg-slate-50/50">
                        <TableCell 
                          className="font-medium cursor-pointer hover:text-[#2C9DE1] hover:underline"
                          onClick={() => navigate(`/classes/${cls.id}/edit`)}
                        >
                          {formatDate(cls.date_completed || cls.date_start)}
                        </TableCell>
                        <TableCell>
                          {cls.class_type === 'inhouse' ? (
                            <span className="text-[#2C9DE1] font-medium" title={cls.company_name || 'InHouse'}>
                              {cls.company_name || 'InHouse'}
                            </span>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">Multi</Badge>
                          )}
                        </TableCell>
                        <TableCell 
                          className="font-medium cursor-pointer hover:text-[#2C9DE1] hover:underline"
                          onClick={() => navigate(`/classes/${cls.id}/edit`)}
                        >
                          {cls.course_name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-slate-600">
                            <GraduationCap className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate max-w-[150px]" title={getInstructorNames(cls.instructor_ids)}>
                              {getInstructorNames(cls.instructor_ids)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            {cls.student_ids?.length || 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const status = getClassStatus(cls);
                            switch (status) {
                              case 'expired':
                                return (
                                  <Badge className="bg-red-100 text-red-800">
                                    <X className="w-3 h-3 mr-1" />
                                    Expired
                                  </Badge>
                                );
                              case 'expiring':
                                return (
                                  <Badge className="bg-amber-100 text-amber-800">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    &lt;90 to Expire
                                  </Badge>
                                );
                              case 'certified':
                                return (
                                  <Badge className="bg-emerald-100 text-emerald-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Certified
                                  </Badge>
                                );
                              case 'completed':
                                return <Badge className="bg-amber-100 text-amber-800">Completed</Badge>;
                              default:
                                return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
                            }
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrainingCenterDetail;
